/**
 * What the message looks like on a phone.
 *
 * **It renders the draft and nothing else.** There is no compile step on this
 * channel — a WhatsApp template is text, a header, a footer and buttons, and
 * WhatsApp itself lays them out — so unlike the email preview there is no server
 * round trip and no risk of a second renderer disagreeing with the first. What
 * this draws is a bubble with the same four parts in the same order Meta sends
 * them.
 *
 * **Positions are shown as positions.** `{{1}}` stays `{{1}}` rather than being
 * substituted with a sample name, because nothing yet records what fills a
 * position and a fabricated “Priya” here would be Wend inventing a value it
 * cannot supply at send time. The one thing a preview must never do is look
 * more finished than the template is.
 */
import { cn } from '@wend/ui';

import { WA_HEADER_FORMAT_LABEL } from '../../../lib/whatsapp-display';
import type { WaDraft } from './wa-draft';

export function WaPreview({ draft }: { draft: WaDraft }) {
  const hasMediaHeader = draft.headerFormat !== 'none' && draft.headerFormat !== 'text';

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-base bg-ink/60 p-4">
        <div className="max-w-[22rem] rounded-lg rounded-tl-sm border border-line bg-white/[0.05] p-3 shadow-sm">
          {hasMediaHeader && (
            <div className="mb-2 grid h-24 place-items-center rounded-base border border-dashed border-line-strong text-[0.75rem] uppercase tracking-[0.1em] text-muted">
              {WA_HEADER_FORMAT_LABEL[draft.headerFormat === 'none' ? 'text' : draft.headerFormat]}{' '}
              header
            </div>
          )}

          {draft.headerFormat === 'text' && draft.headerText.trim() !== '' && (
            <p className="mb-1.5 text-[0.95rem] font-semibold leading-snug text-text">
              {draft.headerText}
            </p>
          )}

          <p className="whitespace-pre-wrap break-words text-[0.92rem] leading-relaxed text-text">
            {draft.body.trim() === '' ? (
              <span className="text-muted">Your message appears here.</span>
            ) : (
              draft.body
            )}
          </p>

          {draft.footer.trim() !== '' && (
            <p className="mt-2 text-[0.78rem] leading-snug text-muted">{draft.footer}</p>
          )}
        </div>

        {draft.buttons.length > 0 && (
          <div className="mt-2 flex max-w-[22rem] flex-col gap-1.5">
            {draft.buttons.map((button, index) => (
              <div
                key={index}
                className={cn(
                  'rounded-lg border border-line bg-white/[0.05] px-3 py-2 text-center',
                  'text-[0.88rem] text-info',
                )}
              >
                {button.text.trim() === '' ? 'Button' : button.text}
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-[0.8rem] leading-snug text-muted">
        Numbered variables are shown as written. Nothing is substituted here, because nothing yet
        records what fills a position.
      </p>
    </div>
  );
}
